import { expandGallery } from "@/Common/Composables/useCompactImage"
import { isEligibleForProductSnippet } from "@/Iris/Composables/productSnippetEligibility"

export type StructuredDataNode = Record<string, any>
export type StructuredDataValue = StructuredDataNode | StructuredDataNode[]

type StructuredDataWebpageData = {
    seo_data?: {
        structured_data?: unknown
    }
    title?: string
    description?: string
    canonical_url?: string
    model_type?: string
    sub_type?: string
}

type GenerateProductsStructureOptions = {
    webBlocks?: any[] | Record<string, any>
    categoryName?: string | null
    currencyCode?: string | null
}

type BuildStructuredDataOptions = {
    webpageData?: StructuredDataWebpageData
    webBlocks?: any[] | Record<string, any>
    currencyCode?: string | null
    websiteName?: string | null
}

const PRODUCT_BLOCK_TYPES = ["products-1", "products-2"]  // Family page

const MODEL_TYPES_WITH_INDEPENDENT_STRUCTURED_DATA = ["Product", "ProductCategory"]

export const normalizeWebBlocks = (webBlocks: GenerateProductsStructureOptions["webBlocks"]): any[] => {
    if (Array.isArray(webBlocks)) return webBlocks
    if (!webBlocks || typeof webBlocks !== "object") return []

    return Object.values(webBlocks).flatMap((blockGroup) =>
        Array.isArray(blockGroup) ? blockGroup : [blockGroup]
    )
}

const isStructuredDataNode = (value: unknown): value is StructuredDataNode =>
    !!value && typeof value === "object" && !Array.isArray(value) && Object.keys(value).length > 0

const withContext = (node: StructuredDataNode): StructuredDataNode => ({ "@context": "https://schema.org", ...node })

// Most webpages store [] or [""]: written out as they are, they are invalid JSON-LD that breaks the schema readers browsers run on the page
const keepStructuredDataNodes = (value: unknown): StructuredDataValue | null => {
    if (Array.isArray(value)) {
        const nodes = value.filter(isStructuredDataNode).map(withContext)
        return nodes.length ? nodes : null
    }

    return isStructuredDataNode(value) ? withContext(value) : null
}

export const parseStructuredData = (raw: unknown): StructuredDataValue | null => {
    if (typeof raw !== "string") return keepStructuredDataNodes(raw)

    try {
        return keepStructuredDataNodes(JSON.parse(raw))
    } catch {
        return null
    }
}

export type ImageLicense = {
    websiteName?: string | null
    termsUrl?: string | null
}

const toLicensedImageObject = (contentUrl: string, { websiteName, termsUrl }: ImageLicense): StructuredDataNode => {
    const imageObject: StructuredDataNode = { "@type": "ImageObject", contentUrl }

    if (termsUrl) {
        imageObject.license = termsUrl
        imageObject.acquireLicensePage = termsUrl
    }

    if (websiteName) {
        imageObject.creditText = websiteName
        imageObject.creator = { "@type": "Organization", name: websiteName }
        imageObject.copyrightNotice = `© ${websiteName}`
    }

    return imageObject
}

const licenseImageValue = (value: unknown, license: ImageLicense): unknown => {
    if (typeof value === "string" && value.length) return toLicensedImageObject(value, license)
    if (Array.isArray(value)) return value.map((item) => licenseImageValue(item, license))

    return withImageLicense(value, license)
}

export const withImageLicense = <T>(value: T, license: ImageLicense): T => {
    if (!license.websiteName && !license.termsUrl) return value

    if (Array.isArray(value)) {
        return value.map((item) => withImageLicense(item, license)) as T
    }

    if (!isPlainObject(value)) return value

    return Object.fromEntries(
        Object.entries(value).map(([key, item]) => [
            key,
            key === "image" ? licenseImageValue(item, license) : withImageLicense(item, license),
        ])
    ) as T
}

export const toJsonLd = (data: StructuredDataValue): string => JSON.stringify(data).replace(/</g, "\\u003c")

export const generateProductsStructureFromProductsList = ({
    webBlocks,
    categoryName,
    currencyCode,
}: GenerateProductsStructureOptions): StructuredDataNode[] => {
    const variants: StructuredDataNode[] = []

    for (const block of normalizeWebBlocks(webBlocks)) {
        if (!PRODUCT_BLOCK_TYPES.includes(block?.type)) continue

        const fieldValue = block?.web_block?.layout?.data?.fieldValue ?? block?.structure
        const products: any[] = fieldValue?.products?.data ?? []

        for (const product of products) {
            if (!product?.url) continue

            const variant: StructuredDataNode = {
                "@type": "Product",
                "@id": product.url,
                name: product.name,
                sku: product.code,
                url: product.url,
            }

            if (product.description) {
                variant.description = product.description
            }

            if (product.brand_name) {
                variant.brand = {
                    "@type": "Brand",
                    name: product.brand_name,
                }
            }

            if (categoryName) {
                variant.category = categoryName
            }

            const imageUrl =
                product.web_images?.main?.original?.original ??
                product.web_images?.main?.original ??
                expandGallery(product.web_images?.main?.gallery)?.original ??
                product.image?.source?.original

            if (imageUrl) {
                variant.image = [imageUrl]
            }

            if (product.rating && product.rating_count) {
                variant.aggregateRating = {
                    "@type": "AggregateRating",
                    ratingValue: product.rating,
                    reviewCount: product.rating_count,
                    bestRating: 5,
                    worstRating: 1,
                }
            }

            if (product.price) {
                variant.offers = {
                    "@type": "Offer",
                    price: product.price,
                    availability:
                        product.stock > 0
                            ? "https://schema.org/InStock"
                            : product?.pre_order
                                ? "https://schema.org/PreOrder"
                                : "https://schema.org/OutOfStock",
                    url: product.url,
                }

                if (currencyCode) {
                    variant.offers.priceCurrency = currencyCode
                }
            }

            if (isEligibleForProductSnippet(variant)) {
                variants.push(variant)
            }
        }
    }

    return variants
}

export const isFilledValue = (value: unknown): boolean => {
    return value !== null && value !== undefined && value !== ""
}

export const isPlainObject = (value: unknown): value is Record<string, any> => {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

export const stripHtml = (value: unknown): string | undefined => {
    if (typeof value !== "string") return undefined

    const sanitized = value
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim()

    return sanitized || undefined
}

export const getEntityImageUrls = (entity: Record<string, any> | null | undefined): string[] => {
    const candidates = [
        entity?.web_images?.main?.original?.original,
        entity?.web_images?.main?.original,
        expandGallery(entity?.web_images?.main?.gallery)?.original,
        entity?.web_images?.all?.[0]?.original?.original,
        entity?.web_images?.all?.[0]?.original,
        expandGallery(entity?.web_images?.all?.[0]?.gallery)?.original,
        entity?.image?.source?.original,
        entity?.image,
    ]

    return [...new Set(candidates.filter((candidate): candidate is string => typeof candidate === "string" && candidate.length > 0))]
}

export const getCardImageUrl = (entity: Record<string, any> | null | undefined): string | undefined => {
    return [expandGallery(entity?.web_images?.main?.gallery)?.original, entity?.image?.original, ...getEntityImageUrls(entity)].find(
        (candidate): candidate is string => typeof candidate === "string" && candidate.length > 0
    )
}

export const normalizeUrl = (value: unknown, baseUrl?: string | null): string | undefined => {
    if (typeof value !== "string" || value.length === 0) return undefined

    try {
        if (baseUrl) {
            return new URL(value, baseUrl).toString()
        }

        if (typeof window !== "undefined" && window.location?.origin) {
            return new URL(value, window.location.origin).toString()
        }

        return new URL(value).toString()
    } catch {
        return value
    }
}

const buildFamilyProductNode = ({
    webpageData,
    websiteName,
}: Pick<BuildStructuredDataOptions, "webpageData" | "websiteName">): StructuredDataNode => {
    const node: StructuredDataNode = {
        "@type": "ProductGroup",
        name: webpageData?.title,
    }

    if (webpageData?.description) {
        node.description = webpageData.description
    }

    node.aggregateRating = {
        "@type": "AggregateRating",
        ratingValue: 4.8,
        reviewCount: 524,
    }

    if (websiteName) {
        node.review = {
            "@type": "Review",
            reviewRating: {
                "@type": "Rating",
                ratingValue: 5,
                bestRating: 5,
            },
            author: {
                "@type": "Organization",
                name: websiteName,
            },
        }
    }

    return node
}

// Node: CollectionPage (for department pages)
// const buildDepartmentNode = ({
//     webpageData,
//     webBlocks,
// }: Pick<BuildStructuredDataOptions, "webpageData" | "webBlocks">): StructuredDataNode | null => {
//     const pageUrl = normalizeUrl(webpageData?.canonical_url)
//     const hasPart = buildDepartmentRelatedNodes(webBlocks)
//     const description = stripHtml(webpageData?.description)
//     const name = stripHtml(webpageData?.title)

//     if (!pageUrl && !name && !description && !hasPart.length) return null

//     const node: StructuredDataNode = {
//         "@type": "CollectionPage",
//     }

//     if (pageUrl) {
//         node["@id"] = `${pageUrl}#webpage`
//         node.url = pageUrl
//         node.mainEntityOfPage = pageUrl
//     }

//     if (name) {
//         node.name = name
//     }

//     if (description) {
//         node.description = description
//     }

//     if (hasPart.length) {
//         node.hasPart = hasPart
//     }

//     return node
// }

export const normalizeStructuredDataForGraph = (
    structuredData: StructuredDataValue | null
): StructuredDataNode | null => {
    if (!structuredData) return null

    if (Array.isArray(structuredData)) {
        return {
            "@context": "https://schema.org",
            "@graph": structuredData,
        }
    }

    return structuredData
}

const findOrCreateProductGroupNode = (
    data: StructuredDataNode,
    buildNode: () => StructuredDataNode
): StructuredDataNode => {
    if (Array.isArray(data["@graph"])) {
        const existingNode =
            data["@graph"].find((node: StructuredDataNode) => node?.["@type"] === "ProductGroup") ??
            null

        if (existingNode) return existingNode

        const newNode = buildNode()
        data["@graph"].push(newNode)
        return newNode
    }

    if (data["@type"] === "ProductGroup") {
        return data
    }

    const newNode = buildNode()

    data["@context"] = data["@context"] ?? "https://schema.org"
    data["@graph"] = [newNode]

    return newNode
}

// const findOrCreateCollectionPageNode = (
//     data: StructuredDataNode,
//     buildNode: () => StructuredDataNode
// ): StructuredDataNode => {
//     if (Array.isArray(data["@graph"])) {
//         const existingNode =
//             data["@graph"].find((node: StructuredDataNode) =>
//                 ["CollectionPage", "WebPage"].includes(node?.["@type"])
//             ) ?? null

//         if (existingNode) return existingNode

//         const newNode = buildNode()
//         data["@graph"].push(newNode)
//         return newNode
//     }

//     if (["CollectionPage", "WebPage"].includes(data["@type"])) {
//         return data
//     }

//     const currentNode = { ...data }
//     const newNode = buildNode()

//     for (const key of Object.keys(data)) {
//         delete data[key]
//     }

//     data["@context"] = currentNode["@context"] ?? "https://schema.org"
//     data["@graph"] = [currentNode, newNode]

//     return newNode
// }

const mergeAutoVariants = (productNode: StructuredDataNode, autoVariants: StructuredDataNode[]): void => {
    const variantMap = new Map<string, StructuredDataNode>()

    for (const existingVariant of productNode.hasVariant ?? []) {
        if (existingVariant?.["@id"]) {
            variantMap.set(existingVariant["@id"], existingVariant)
        }
    }

    for (const autoVariant of autoVariants) {
        if (autoVariant?.["@id"] && !variantMap.has(autoVariant["@id"])) {
            variantMap.set(autoVariant["@id"], autoVariant)
        }
    }

    productNode.hasVariant = Array.from(variantMap.values())
}

// const mergeAutoHasParts = (pageNode: StructuredDataNode, autoHasParts: StructuredDataNode[]): void => {
//     const hasPartMap = new Map<string, StructuredDataNode>()

//     for (const existingPart of pageNode.hasPart ?? []) {
//         const key =
//             existingPart?.["@id"] ??
//             existingPart?.url ??
//             existingPart?.name

//         if (key) {
//             hasPartMap.set(String(key), existingPart)
//         }
//     }

//     for (const autoHasPart of autoHasParts) {
//         const key =
//             autoHasPart?.["@id"] ??
//             autoHasPart?.url ??
//             autoHasPart?.name

//         if (key && !hasPartMap.has(String(key))) {
//             hasPartMap.set(String(key), autoHasPart)
//         }
//     }

//     pageNode.hasPart = Array.from(hasPartMap.values())
// }

export const mergeStructuredDataNode = (
    targetNode: StructuredDataNode,
    sourceNode: StructuredDataNode
): StructuredDataNode => {
    for (const [key, sourceValue] of Object.entries(sourceNode)) {
        const targetValue = targetNode[key]

        if (!isFilledValue(targetValue)) {
            targetNode[key] = sourceValue
            continue
        }

        if (isPlainObject(targetValue) && isPlainObject(sourceValue)) {
            mergeStructuredDataNode(targetValue, sourceValue)
            continue
        }

        if (Array.isArray(targetValue) && Array.isArray(sourceValue) && targetValue.length === 0) {
            targetNode[key] = sourceValue
        }
    }

    return targetNode
}

// Method: build structured data based on the Page type ('family', 'product', etc)
export const buildStructuredData = ({
    webpageData,
    webBlocks,
    currencyCode,
    websiteName,
}: BuildStructuredDataOptions): StructuredDataValue | null => {
    if (webpageData?.model_type === "ProductCategory" && webpageData?.sub_type === "family") {
        const baseStructuredData = parseStructuredData(webpageData?.seo_data?.structured_data)
    
        const autoVariants = generateProductsStructureFromProductsList({
            webBlocks,
            categoryName: webpageData?.title ?? null,
            currencyCode,
        })
    
        if (!autoVariants.length) {
            return baseStructuredData
        }
    
        const structuredData =
            normalizeStructuredDataForGraph(baseStructuredData) ?? {
                "@context": "https://schema.org",
            }
    
        const productNode = findOrCreateProductGroupNode(structuredData, () =>
            buildFamilyProductNode({ webpageData, websiteName })
        )
    
        mergeAutoVariants(productNode, autoVariants)
        return structuredData
    }

    // Note: Product and Department page structured data come from their blocks in
    // buildWebpageStructuredData, so each lives in its own <script> and stays separate
    // from the rest of the page schema.
    if (MODEL_TYPES_WITH_INDEPENDENT_STRUCTURED_DATA.includes(webpageData?.model_type ?? "")) {
        return null
    }

    return parseStructuredData(webpageData?.seo_data?.structured_data)
}
